'use client';

import { FeatherEdit2, FeatherPlus, FeatherTag } from '@subframe/core';
import { useState } from 'react';
import { Badge, Button, ConfirmationModal, DataTable, Switch } from '@/shared/components/ui/common';
import type { ColumnDef, RowAction } from '@/shared/components/ui/common';
import { useToast } from '@/shared/providers';
import { getErrorMessage } from '@/shared/utils/error';
import { useAppDispatch } from '@/store/hooks';
import { useSituacoesArbitral } from '../hooks/useSituacoesArbitral';
import { alterarAtivacaoSituacaoArbitral } from '../store';
import type { SituacaoArbitral } from '../types/situacaoArbitralTypes';
import { resumoEscopo, statusPaiLabel } from '../utils/situacaoArbitralUtils';
import { SituacaoFormDrawer } from './SituacaoFormDrawer';
export function SituacoesArbitralContainer() {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const {
    situacoes,
    escritorios,
    loading,
    error,
    recarregar
  } = useSituacoesArbitral();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [emEdicao, setEmEdicao] = useState<SituacaoArbitral | null>(null);
  const [aDesativar, setADesativar] = useState<SituacaoArbitral | null>(null);
  const abrirNova = () => {
    setEmEdicao(null);
    setDrawerOpen(true);
  };
  const abrirEdicao = (situacao: SituacaoArbitral) => {
    setEmEdicao(situacao);
    setDrawerOpen(true);
  };
  const alterarAtivacao = async (situacao_0: SituacaoArbitral, active: boolean) => {
    try {
      await dispatch(alterarAtivacaoSituacaoArbitral({
        id: situacao_0.id,
        active
      })).unwrap();
      toast.showSuccess(active ? 'Situação ativada' : 'Situação desativada', `"${situacao_0.label}" foi ${active ? 'ativada' : 'desativada'}.`);
    } catch (err) {
      toast.showError('Erro ao alterar situação', getErrorMessage(err));
    }
  };
  const handleToggle = (situacao_1: SituacaoArbitral, checked: boolean) => {
    if (checked) {
      void alterarAtivacao(situacao_1, true);
    } else {
      setADesativar(situacao_1);
    }
  };
  const confirmarDesativacao = () => {
    if (!aDesativar) return;
    const alvo = aDesativar;
    setADesativar(null);
    void alterarAtivacao(alvo, false);
  };
  const columns: ColumnDef<SituacaoArbitral>[] = [{
    id: 'label',
    header: 'Nome',
    accessorKey: 'label',
    sortable: true
  }, {
    id: 'key',
    header: 'Chave',
    cell: row => <span className="text-caption text-subtext-color font-mono">{row.key}</span>
  }, {
    id: 'statusPai',
    header: 'Status relacionado',
    cell: row_0 => statusPaiLabel(row_0.statusPai)
  }, {
    id: 'escopo',
    header: 'Escopo',
    cell: row_1 => <Badge variant={row_1.isShared ? 'neutral' : 'brand'}>{resumoEscopo(row_1)}</Badge>
  }, {
    id: 'ativo',
    header: 'Ativa',
    align: 'center',
    cell: row_2 => <Switch checked={row_2.active} onCheckedChange={checked_0 => handleToggle(row_2, checked_0)} />
  }];
  const rowActions: RowAction<SituacaoArbitral>[] = [{
    id: 'editar',
    label: 'Editar',
    icon: <FeatherEdit2 />,
    onClick: abrirEdicao
  }];
  return <>
      <DataTable data={situacoes} columns={columns} getRowId={row_3 => row_3.id} isLoading={loading} rowActions={rowActions} card={{
      title: 'Situações',
      headerActions: <Button icon={<FeatherPlus />} onClick={abrirNova}>
              Nova situação
            </Button>
    }} error={error ? {
      title: 'Erro ao carregar situações',
      description: error,
      retryAction: recarregar
    } : null} emptyState={{
      icon: <FeatherTag />,
      title: 'Nenhuma situação cadastrada',
      description: 'Crie situações para sinalizar o que os processos estão aguardando.',
      action: <Button icon={<FeatherPlus />} onClick={abrirNova}>
              Nova situação
            </Button>
    }} />

      <SituacaoFormDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} situacao={emEdicao} escritorios={escritorios} />

      <ConfirmationModal isOpen={Boolean(aDesativar)} onOpenChange={open => {
      if (!open) setADesativar(null);
    }} title="Desativar situação" description={`"${aDesativar?.label ?? ''}" deixará de aparecer para novos vínculos. Processos que já a possuem não são afetados.`} confirmText="Desativar" variant="destructive" onConfirm={confirmarDesativacao} />
    </>;
}